import type { LucideIcon } from 'lucide-react';
import {
  FileText,
  MessageCircle,
  Paperclip,
  Replace,
  SlidersHorizontal,
} from 'lucide-react';
import { defineMessages, type MessageDescriptor } from 'react-intl';

import type { ImproveNotesTaskId } from '../../../contracts/post-note-quality';

/**
 * Re-exported at its historical name so every importer of this module — the
 * app's endpoint hub included — keeps binding `ImproveNotesTaskId` from here.
 * The DECLARATION is in `/contracts`, because the identity crosses zones.
 */
export type { ImproveNotesTaskId };

export interface ImproveNotesTaskDefinition {
  id: ImproveNotesTaskId;
  icon: LucideIcon;
  title: MessageDescriptor;
  description: MessageDescriptor;
  /** Call-to-action label. The button is a no-op in this ticket — wiring the
   * feature-opening behaviour is GRO-3269. */
  cta: MessageDescriptor;
}

export interface ImproveNotesTask extends ImproveNotesTaskDefinition {
  /**
   * Whether the task is complete. Derived per-user in `useImproveNotesPanel`
   * from `post_note_quality_completed_tasks` on the user record (GRO-3270);
   * "Create a note" is always complete because the panel only renders once a
   * note exists.
   */
  completed: boolean;
}

const messages = defineMessages({
  createNoteTitle: {
    id: 'CzogPZpUmk',
    defaultMessage: 'Create a note',
    description: 'Improve your notes panel — Create a note task title',
  },
  createNoteDescription: {
    id: 'jNOMchkU0n',
    defaultMessage:
      'Generate a structured note using our library of templates, or create your own.',
    description: 'Improve your notes panel — Create a note task description',
  },
  createNoteCta: {
    id: 'N3UayQbeAq',
    defaultMessage: 'Get started',
    description: 'Improve your notes panel — Create a note task CTA',
  },
  addContextTitle: {
    id: 'sMfBlGWYnh',
    defaultMessage: 'Add context',
    description: 'Improve your notes panel — Add context task title',
  },
  addContextDescription: {
    id: 'yy+q8Cv7bA',
    defaultMessage: 'Add context to enrich your note.',
    description: 'Improve your notes panel — Add context task description',
  },
  addContextCta: {
    id: 'nMjKdEK2Dx',
    defaultMessage: 'Try now',
    description: 'Improve your notes panel — Add context task CTA',
  },
  customiseTitle: {
    id: 'xAARUr1mAd',
    defaultMessage: 'Customise length & tone',
    description: 'Improve your notes panel — Customise length & tone task title',
  },
  customiseDescription: {
    id: 'jG5HhqewWO',
    defaultMessage: 'Set your default length and tone.',
    description: 'Improve your notes panel — Customise length & tone task description',
  },
  customiseCta: {
    id: 'hu/8ZYTgsE',
    defaultMessage: 'Set defaults',
    description: 'Improve your notes panel — Customise length & tone task CTA',
  },
  askHeidiTitle: {
    id: '3A9umJWIDT',
    defaultMessage: 'Ask Heidi',
    description: 'Improve your notes panel — Ask Heidi task title',
  },
  askHeidiDescription: {
    id: 'uJciSPdI3X',
    defaultMessage: 'Rewrite, shorten, or change any section.',
    description: 'Improve your notes panel — Ask Heidi task description',
  },
  askHeidiCta: {
    id: 'Ucv8FqWfl/',
    defaultMessage: 'Ask Heidi',
    description: 'Improve your notes panel — Ask Heidi task CTA',
  },
  replaceWordsTitle: {
    id: '+doxwVEChF',
    defaultMessage: 'Replace words & phrases',
    description: 'Improve your notes panel — Replace words & phrases task title',
  },
  replaceWordsDescription: {
    id: 'a//qOaA9W1',
    defaultMessage: "Swap words or phrases you don't use.",
    description: 'Improve your notes panel — Replace words & phrases task description',
  },
  replaceWordsCta: {
    id: 'cDVCgbAqc5',
    defaultMessage: 'Replace',
    description: 'Improve your notes panel — Replace words & phrases task CTA',
  },
});

/**
 * The five note-improvement task definitions in canonical order. Completion
 * is derived per-user in `useImproveNotesPanel` (GRO-3270); "Create a note"
 * is always complete because the panel only renders once a note exists.
 */
export const IMPROVE_NOTES_TASK_DEFINITIONS: readonly ImproveNotesTaskDefinition[] = [
  {
    id: 'create-note',
    icon: FileText,
    title: messages.createNoteTitle,
    description: messages.createNoteDescription,
    cta: messages.createNoteCta,
  },
  {
    id: 'add-context',
    icon: Paperclip,
    title: messages.addContextTitle,
    description: messages.addContextDescription,
    cta: messages.addContextCta,
  },
  {
    id: 'customise',
    icon: SlidersHorizontal,
    title: messages.customiseTitle,
    description: messages.customiseDescription,
    cta: messages.customiseCta,
  },
  {
    id: 'ask-heidi',
    icon: MessageCircle,
    title: messages.askHeidiTitle,
    description: messages.askHeidiDescription,
    cta: messages.askHeidiCta,
  },
  {
    id: 'replace-words',
    icon: Replace,
    title: messages.replaceWordsTitle,
    description: messages.replaceWordsDescription,
    cta: messages.replaceWordsCta,
  },
] as const;

/**
 * Resolves task definitions against the user's completed task ids.
 * "Create a note" is always complete (the panel only renders post-note).
 */
export const resolveImproveNotesTasks = (
  completedTaskIds: readonly string[] | undefined
): ImproveNotesTask[] =>
  IMPROVE_NOTES_TASK_DEFINITIONS.map((definition) => ({
    ...definition,
    completed:
      definition.id === 'create-note' ||
      (completedTaskIds?.includes(definition.id) ?? false),
  }));

/**
 * Whether every "Improve your notes" task is complete for the given user
 * (GRO-3284). Accounts for "create-note" being implicitly complete post-note.
 * Used to gate the gamified trial in the `sequential` arm and to detect the
 * all-tasks-complete reward milestone.
 */
export const areAllImproveNotesTasksComplete = (
  completedTaskIds: readonly string[] | undefined
): boolean => resolveImproveNotesTasks(completedTaskIds).every((task) => task.completed);

/**
 * Orders tasks for display: incomplete tasks pinned to the top in canonical
 * order, completed tasks sink to the bottom in canonical order. With no
 * tasks completed yet this yields:
 * add-context, customise, ask-heidi, replace-words, create-note.
 */
export const orderImproveNotesTasks = (
  tasks: readonly ImproveNotesTask[]
): ImproveNotesTask[] => [
  ...tasks.filter((task) => !task.completed),
  ...tasks.filter((task) => task.completed),
];
